import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import type { MatterShareFolderFile } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";

type ShareFolderTreeItem = MatterShareFolderFile & {
  name: string;
  isFolder: boolean;
  idLocation: string;
  isSharedFolder?: boolean;
  matterFileId: string;
  matterFileName: string;
  matterFileIsFolder: boolean;
  matterFileIdLocation: string;
  matterShareFolderId: string;
  matterShareFolderFileId: string;
  matterShareFolderFilePresignedUrlKey: string;
};

export const useShareFolderTreeData = (matterId: string, enabled = true) => {
  const { data: matterData } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: enabled && Boolean(matterId),
  });
  const matter = matterData?.data;

  if (!matter) {
    return { items: [], folders: [] };
  }

  // FastAPI returns each folder with its associated contact connections.
  const folders = matter.shareFolders.map((f) => ({
    ...f,
    files: [] as (typeof matter.shareFolderFiles)[number][],
  }));

  const byId = new Map<string, (typeof folders)[number]>();
  for (const f of folders) {
    byId.set(f.id, f);
  }

  // Attach files
  for (const file of matter.shareFolderFiles) {
    const parent = file.shareFolderId
      ? byId.get(file.shareFolderId)
      : undefined;
    if (parent) parent.files.push({ ...file });
  }

  // Flatten ONLY folders + files for the tree UI
  const items: ShareFolderTreeItem[] = [];

  const sharedFoldersParentId = "shared-folders-root";
  if (folders.length > 0) {
    const firstFolder = folders[0];
    items.push({
      dateCreated: firstFolder.dateCreated,
      dateModified: firstFolder.dateModified,
      fileId: "",
      shareFolderId: "",
      id: sharedFoldersParentId,
      firmId: firstFolder.firmId,
      matterId: matter.id,
      presignedUrlKey: "",
      name: "Shared folders",
      isFolder: true,
      idLocation: "",
      matterFileId: sharedFoldersParentId,
      matterFileName: "Shared Folders",
      matterFileIsFolder: true,
      matterFileIdLocation: "",
      matterShareFolderId: "",
      matterShareFolderFileId: "",
      matterShareFolderFilePresignedUrlKey: "",
      isSharedFolder: true,
    });
  }

  for (const sf of folders) {
    const folderId = sf.id;
    const folderName = sf.label.trim() || "Untitled Share Folder";

    // Share folder node
    items.push({
      dateCreated: sf.dateCreated,
      dateModified: sf.dateModified,
      fileId: "",
      shareFolderId: sf.id,
      id: folderId,
      firmId: sf.firmId,
      matterId: sf.matterId,
      presignedUrlKey: "",
      name: folderName,
      isFolder: true,
      idLocation: sharedFoldersParentId,
      matterFileId: folderId,
      matterFileName: folderName,
      matterFileIsFolder: true,
      matterFileIdLocation: sharedFoldersParentId,
      matterShareFolderId: sf.id,
      matterShareFolderFileId: "",
      matterShareFolderFilePresignedUrlKey: "",
      isSharedFolder: true,
    });

    // Files under the share folder
    for (const f of sf.files) {
      const sourceFile = matter.files.find((file) => file.id === f.fileId);
      if (!sourceFile) continue;

      items.push({
        ...f,
        name: sourceFile.name,
        isFolder: false,
        idLocation: folderId,
        presignedUrlKey: f.presignedUrlKey ?? "",
        matterFileId: sourceFile.id,
        matterFileName: sourceFile.name,
        matterFileIsFolder: false,
        matterFileIdLocation: folderId,
        matterShareFolderId: sf.id,
        matterShareFolderFileId: f.id,
        matterShareFolderFilePresignedUrlKey: f.presignedUrlKey ?? "",
        isSharedFolder: false,
      });
    }
  }

  // Return both: UI tree + structured folders (with connections as data)
  return { items, folders };
};
